import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ReviewStatus, ReviewFilters as IReviewFilters } from '@/types';
import { Search, Filter, X, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

const APPROVAL_FILTER_VALUES = ['autoApproved', 'manuallyApproved', 'manualReviewRequired', 'wordFilterFlagged'] as const;

interface ReviewFiltersProps {
  filters: IReviewFilters;
  onFiltersChange: (filters: IReviewFilters) => void;
}

export function ReviewFilters({ filters, onFiltersChange }: ReviewFiltersProps) {
  const { t } = useTranslation('reviews');
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [dateError, setDateError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const filtersRef = useRef(filters);
  const onFiltersChangeRef = useRef(onFiltersChange);

  useEffect(() => {
    filtersRef.current = filters;
    onFiltersChangeRef.current = onFiltersChange;
  });

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const applySearch = useCallback((value: string) => {
    clearTimeout(debounceRef.current);
    onFiltersChangeRef.current({ ...filtersRef.current, search: value || undefined });
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    clearTimeout(debounceRef.current);
    if (value.length >= 3 || value.length === 0) {
      debounceRef.current = setTimeout(() => applySearch(value), 300);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      applySearch(searchValue);
    }
  };

  const handleStatusChange = (status: string) => {
    const currentStatus = filters.status || [];
    const newStatus = currentStatus.includes(status as ReviewStatus)
      ? currentStatus.filter((s) => s !== status)
      : [...currentStatus, status as ReviewStatus];
    onFiltersChange({ ...filters, status: newStatus.length ? newStatus : undefined });
  };

  const handleApprovalFilterChange = (value: string) => {
    const current = filters.approvalFilter || [];
    const newValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onFiltersChange({
      ...filters,
      approvalFilter: newValues.length ? newValues : undefined,
    });
  };

  const handleRatingChange = (rating: string) => {
    const ratingNum = parseInt(rating);
    const currentRatings = filters.rating || [];
    const newRatings = currentRatings.includes(ratingNum)
      ? currentRatings.filter((r) => r !== ratingNum)
      : [...currentRatings, ratingNum];
    onFiltersChange({
      ...filters,
      rating: newRatings.length ? newRatings : undefined,
    });
  };

  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const from = field === 'dateFrom' ? value : filters.dateFrom;
    const to = field === 'dateTo' ? value : filters.dateTo;

    if (from && to && to < from) {
      setDateError(t('filters.dateValidationError'));
    } else {
      setDateError('');
    }

    onFiltersChange({ ...filters, [field]: value || undefined });
  };

  const clearAllFilters = () => {
    setSearchValue('');
    setDateError('');
    onFiltersChange({});
  };

  const resetFilters = () => {
    setDateError('');
    onFiltersChange({ search: filters.search });
  };

  const activeFilterCount = [
    filters.status?.length || 0,
    filters.approvalFilter?.length || 0,
    filters.rating?.length || 0,
    filters.dateFrom ? 1 : 0,
    filters.dateTo ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const hasActiveFilters = activeFilterCount > 0 || !!filters.search;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('filters.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(hasActiveFilters && 'border-primary')}
        >
          <Filter className="h-4 w-4 mr-2" />
          {t('filters.filtersButton')}
          {activeFilterCount > 0 && (
            <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="icon" onClick={clearAllFilters}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filters.status?.map((status) => (
            <Badge
              key={status}
              variant="secondary"
              className="gap-1 pl-2.5 pr-1.5 py-1 text-xs font-normal cursor-default"
            >
              {t('filters.reviewStatus')}: {t(`filters.reviewStatusValues.${status}`)}
              <button
                onClick={() => handleStatusChange(status)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {filters.approvalFilter?.map((value) => (
            <Badge
              key={value}
              variant="secondary"
              className="gap-1 pl-2.5 pr-1.5 py-1 text-xs font-normal cursor-default"
            >
              {t('filters.approvalFilter')}: {t(`filters.approvalFilterValues.${value}` as any)}
              <button
                onClick={() => handleApprovalFilterChange(value)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {filters.rating?.map((rating) => (
            <Badge
              key={rating}
              variant="secondary"
              className="gap-1 pl-2.5 pr-1.5 py-1 text-xs font-normal cursor-default"
            >
              {'★'.repeat(rating)}
              {'☆'.repeat(5 - rating)}
              <button
                onClick={() => handleRatingChange(rating.toString())}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {filters.dateFrom && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1.5 py-1 text-xs font-normal cursor-default"
            >
              {t('filters.dateFrom')}: {filters.dateFrom}
              <button
                onClick={() => handleDateChange('dateFrom', '')}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filters.dateTo && (
            <Badge
              variant="secondary"
              className="gap-1 pl-2.5 pr-1.5 py-1 text-xs font-normal cursor-default"
            >
              {t('filters.dateTo')}: {filters.dateTo}
              <button
                onClick={() => handleDateChange('dateTo', '')}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {isExpanded && (
        <div className="p-4 border rounded-lg bg-muted/50 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-[0.7rem] font-medium uppercase text-muted-foreground mb-2 block">{t('filters.reviewStatus')}</label>
              <div className="space-y-2">
                {Object.values(ReviewStatus).map((status) => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.status?.includes(status) || false}
                      onChange={() => handleStatusChange(status)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{t(`filters.reviewStatusValues.${status}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[0.7rem] font-medium uppercase text-muted-foreground mb-2 block">{t('filters.approvalFilter')}</label>
              <div className="space-y-2">
                {APPROVAL_FILTER_VALUES.map((value) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.approvalFilter?.includes(value) || false}
                      onChange={() => handleApprovalFilterChange(value)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{t(`filters.approvalFilterValues.${value}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[0.7rem] font-medium uppercase text-muted-foreground mb-2 block">{t('filters.rating')}</label>
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((rating) => (
                  <label key={rating} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.rating?.includes(rating) || false}
                      onChange={() => handleRatingChange(rating.toString())}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">
                      {'★'.repeat(rating)}
                      {'☆'.repeat(5 - rating)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[0.7rem] font-medium uppercase text-muted-foreground mb-2 block">{t('filters.dateRange')}</label>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">{t('filters.dateFrom')}</label>
                  <Input
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">{t('filters.dateTo')}</label>
                  <Input
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) => handleDateChange('dateTo', e.target.value)}
                  />
                </div>
                {dateError && (
                  <p className="text-xs text-destructive font-medium">{dateError}</p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <Button variant="outline" size="sm" onClick={resetFilters} className="gap-2">
              <RotateCcw className="h-3.5 w-3.5" />
              {t('filters.resetFilters')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
